import * as ko from 'knockout';
import i18n from '../../i18n';

import { BaseTrialStep } from './base';
import { WizardController } from '../../screens/trial_wizard';
import { session } from '../../session';
import {
  TrialState,
  getTrialStateLabel,
  isAuditedStateChange,
  STATE_CHANGE_REASON_MAX_LENGTH,
} from '../../models/TrialState';
import {
  canCreateTrial,
  customPermissionsEnabled,
  isArchiveLocked,
  PermissionCodename,
  requireReasonForTrialStateChange,
} from '../../permissions';

let template =
  require('raw-loader!../../../templates/components/trial_wizard/trial_activation.html').default;

class TrialActivation extends BaseTrialStep {
  // @ts-ignore
  private title = session.isTreatmentManagementEnabledForTrial(this.trialWizard().trial())
    ? i18n.t('Step 10 - Activation')()
    : i18n.t('Step 8 - Activation')();

  private initialState = ko.observable<TrialState>(TrialState.Draft); // the last known saved state

  // the currently selected state, might be not saved yet
  state = ko.computed<TrialState>({
    read: () => {
      return this.trialWizard().trial().state();
    },
    write: (value: string) => {
      this.trialWizard()
        .trial()
        .state(value as TrialState);
      this.showError(false);
      this.showReasonError(false);
    },
  });

  cancellationReason = ko.computed<string>({
    read: () => {
      return this.trialWizard().trial().cancellationReason();
    },
    write: (value: string) => {
      this.trialWizard().trial().cancellationReason(value);
      this.showReasonError(false);
    },
  });

  stateChangeReason = ko.computed<string>({
    read: () => {
      return this.trialWizard().trial().stateChangeReason();
    },
    write: (value: string) => {
      this.trialWizard().trial().stateChangeReason(value);
      this.showReasonError(false);
    },
  });

  private needsReasonForSelectedState = ko.pureComputed(
    () => requireReasonForTrialStateChange() && isAuditedStateChange(this.initialState(), this.state())
  );

  needsStateChangeReason = ko.pureComputed(
    () => this.needsReasonForSelectedState() && this.state() !== TrialState.Canceled
  );

  needsCancellationReason = ko.pureComputed(
    () => this.needsReasonForSelectedState() && this.state() === TrialState.Canceled
  );
  template = ko.pureComputed(() => {
    let trial = this.trialWizard().trial();
    return trial ? trial.template : false;
  });

  staffAssignmentPageUrl = ko.pureComputed(() => {
    return session.toTenantPath(this.trialWizard().trial().assignStaffUrl());
  });

  showArchivedNotice = ko.pureComputed(() => {
    const trial = this.trialWizard().trial();
    return trial ? isArchiveLocked(trial) : false;
  });

  showUnarchivedNotice = ko.pureComputed(() => {
    const trial = this.trialWizard().trial();
    return trial ? trial.glpFieldRestricted() && !trial.archived() : false;
  });

  showError = ko.observable<boolean>(false);
  showReasonError = ko.observable<boolean>(false);
  stateChangeReasonMaxLength = STATE_CHANGE_REASON_MAX_LENGTH;

  trialStates = ko.pureComputed(() => {
    const draftStates = [TrialState.Draft, TrialState.Preview, TrialState.Approved];
    const isDraftState = draftStates.includes(this.initialState());

    const states = [TrialState.Paused];

    if (!this.template()) {
      states.unshift(TrialState.Active);

      if (this.initialState() === TrialState.Completed) {
        states.push(TrialState.Completed);
      }

      if (this.initialState() === TrialState.Completed || this.initialState() === TrialState.DataValidated) {
        states.push(TrialState.DataValidated);
      }

      if (
        isDraftState ||
        this.initialState() === TrialState.Active ||
        this.initialState() === TrialState.Canceled
      ) {
        states.push(TrialState.Canceled);
      }
    }

    // templates can go paused -> draft
    if (this.template() || isDraftState) {
      states.unshift(TrialState.Draft, TrialState.Preview);
    }
    if (this.template() && isDraftState) {
      states.splice(states.length - 1, 0, TrialState.Approved);
    }

    return states.map((state) => ({
      name: getTrialStateLabel(state),
      value: state,
    }));
  });

  constructor(params: { controller: WizardController }) {
    super(params);

    let trial = this.trialWizard().trial();
    if (trial.id()) {
      this.initialState(trial.state());
    }

    this.ready(true);
  }

  dispose() {
    this.state.dispose();
    this.cancellationReason.dispose();
    this.stateChangeReason.dispose();
    this.needsReasonForSelectedState.dispose();
    this.needsStateChangeReason.dispose();
    this.needsCancellationReason.dispose();
  }

  isStateEnabled(state: { value: string }): boolean {
    const trial = this.trialWizard().trial();
    if (!trial || isArchiveLocked(trial)) {
      return false;
    }

    const currentTrialState = trial.state();
    const currentUser = this.trialWizard().userData;

    if (customPermissionsEnabled()) {
      const codename = trial.template ? PermissionCodename.CHANGE_TEMPLATE : PermissionCodename.CHANGE_TRIAL;
      // The wizard saves once, at the end, so a trial being created has no id yet and is gated on
      // add_trial/add_template rather than change_*. Asking for change_* here would leave a role
      // that may create trials able to produce nothing but drafts.
      const allowed = trial.id() ? session.hasPermission(codename) : canCreateTrial(trial.template);
      // Preview pushes the trial to the mobile app, which needs a crop. The legacy branch below
      // applies the same guard, and the template keys its "a crop is required" hint off this
      // method, so dropping it here would offer an unexplained, unusable radio.
      return allowed && !(state.value === TrialState.Preview && !trial.crop());
    }

    if (currentUser.role === 'admin') {
      return true;
    }

    if (trial.template) {
      if (currentUser.role === 'template_editor') {
        return trial.owners().some((owner) => owner.id === currentUser.id);
      } else if (
        currentTrialState === TrialState.Approved &&
        state.value === TrialState.Paused &&
        (currentUser.role === 'editor' || currentUser.role === 'manager') &&
        trial.owners().some((owner) => owner.id === currentUser.id)
      ) {
        return true;
      }
    }

    if (
      [TrialState.Draft, TrialState.Preview, TrialState.Active].includes(state.value as TrialState) &&
      this.allowEditAnyLimited()
    ) {
      return true;
    }

    return this.allowEditAny() && !(state.value === TrialState.Preview && !trial.crop());
  }

  hasErrors(): boolean {
    return this.showError() || this.showReasonError();
  }

  applyLocalValidation(): boolean {
    let isValid = !!this.state();

    this.showError(!isValid);

    const reasonMissing =
      (this.needsStateChangeReason() && this.stateChangeReason().trim() === '') ||
      (this.needsCancellationReason() && this.cancellationReason().trim() === '');
    this.showReasonError(reasonMissing);

    return !reasonMissing;
  }

  didSave() {
    this.initialState(this.state());
  }
}

ko.components.register('trial-activation', {
  viewModel: TrialActivation,
  template: template,
});
